import { useCallback } from "react";
import { AxiosError } from "axios";
import { useSnackbar } from "@powerdiary/components-library/lib";
import { DateTime } from "luxon";
import {
    AppointmentBookingFailedApiDetailsErrorResponse,
    AppointmentBookingRequest,
    AppointmentBookingValidationResponse,
} from "types/generated/models";
import { usePostMutation, useDefaultHandleAxiosErrorProps } from "hooks/useClientPortalQuery";
import { handleAxiosError } from "hooks/useClientPortalQuery.helpers";
import { useAuthorizationContext } from "../../../context";
import { clearBookingState, selectBooking, useClientPortalDispatch, useClientPortalSelector } from "../../../redux";
import { useAppointmentBookingApi } from "../../../hooks/useAppointmentBookingApi";

export interface IValidateBookingOptions {
    /** Report nothing to the user; the caller decides what a failure means. */
    silent?: boolean;
}

export type ValidateBookingFunction = (
    options?: IValidateBookingOptions
) => Promise<AppointmentBookingValidationResponse>;

function isExpectedValidationError(error: AxiosError): boolean {
    const status = error.response?.status;
    return status !== undefined && status >= 400 && status < 500 && status !== 401 && status !== 404;
}

export function useValidateBooking() {
    const { getEndpoint } = useAppointmentBookingApi();
    const { userDetails } = useAuthorizationContext();
    const { location, service, provider, availability, confirmation, session } = useClientPortalSelector(selectBooking);
    const dispatch = useClientPortalDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const defaultErrorProps = useDefaultHandleAxiosErrorProps();

    const { mutateAsync } = usePostMutation<
        AppointmentBookingValidationResponse,
        AxiosError<AppointmentBookingFailedApiDetailsErrorResponse>,
        AppointmentBookingRequest
    >({
        url: getEndpoint("ValidateBooking"),
        // Reported in validateBooking, which knows whether the caller wants the user told.
        handleCustomError: () => undefined,
    });

    const validateBooking = useCallback<ValidateBookingFunction>(
        async (options) => {
            const locationId = location.data?.locationId;
            const timeEntry = availability.data?.timeEntry;
            const notes = confirmation.data?.notes;
            const clientId = confirmation.data?.client?.id ?? userDetails?.clientId;
            const bookingType = session.bookingType;

            if (!locationId || !timeEntry?.dateTimeOffset) {
                throw new Error("Missing required booking data");
            }

            const dateTime = DateTime.fromISO(timeEntry.dateTimeOffset);
            const dateForAPI = dateTime.toISO({ includeOffset: false });

            const validationRequest: AppointmentBookingRequest = {
                clientId: clientId,
                locationId,
                serviceId: service.data?.serviceId,
                providerId: provider.data?.providerId,
                appointmentId: timeEntry.appointmentId,
                date: dateForAPI,
                offset: dateTime.offset,
                durationMinutes: timeEntry.duration,
                type: bookingType,
                notes: notes || undefined,
            };

            try {
                return await mutateAsync(validationRequest);
            } catch (error_) {
                const axiosError = error_ as AxiosError<AppointmentBookingFailedApiDetailsErrorResponse>;

                if (options?.silent) {
                    throw error_;
                }

                if (isExpectedValidationError(axiosError)) {
                    const message = axiosError?.response?.data?.message || "Booking validation failed";

                    enqueueSnackbar(message, { variant: "error" });

                    if (axiosError?.response?.data?.details?.resetBooking) {
                        dispatch(clearBookingState());
                    }
                } else {
                    handleAxiosError({ ...defaultErrorProps, error: axiosError });
                }

                throw error_;
            }
        },
        [
            location.data,
            availability.data,
            confirmation.data,
            userDetails,
            session.bookingType,
            service.data,
            provider.data,
            mutateAsync,
            enqueueSnackbar,
            dispatch,
            defaultErrorProps,
        ]
    );

    return { validateBooking };
}
